import type { ReactNode } from "react";
import { useEffect, useState } from "react";

/**
 * Three cards, the first time somebody opens the portal, and then never again.
 *
 * WHY THREE AND NOT A PRODUCT TOUR. A surveyor opens this on a tablet in
 * somebody's hallway, having been handed a login by their employer, with no
 * training and a householder waiting. What they need is not a feature list:
 * it is to know that the list in front of them is surveys, that each one tells
 * them the next thing to do, and that nothing here can be broken. That is
 * three sentences, so it is three cards.
 *
 * WHY IT IS DISMISSED FOR EVER, AND IN localStorage. Seen-ness is a property
 * of this browser and this person, not of the organisation's data: a second
 * surveyor on the same tablet has not seen it, and somebody who clears their
 * browser has lost nothing that matters. Putting it on the server would mean a
 * migration, a column and a write on every page load to answer a question
 * whose wrong answer costs one dismissal.
 *
 * NOT A MODAL OVER THE WHOLE SCREEN. It sits above the list, so the work is
 * visible behind the explanation of it and Skip is never the only way out —
 * every tour anybody has ever met is dismissed unread, and this one is
 * dismissed unread beside the thing it describes.
 */

const SEEN = "sts.tour.surveys.v1";

const CARDS: readonly { readonly title: string; readonly body: string }[] = [
  {
    title: "This is every survey",
    body:
      "One list, newest first. A blue dot means nobody has opened it since it arrived from the phone. Tap a house to open its survey.",
  },
  {
    title: "Each survey says the next thing to do",
    body:
      "Open one and the box at the top is what it needs. If that box is empty, that survey is finished and ready to hand over.",
  },
  {
    title: "Nothing here can go badly wrong",
    body:
      "Every change says what happened and can be undone. Deleting is the one thing that cannot, so it asks first and tells you what would be lost.",
  },
];

/**
 * Whether this browser has been shown the tour.
 *
 * Wrapped, because `localStorage` THROWS rather than returning null in a
 * private window and with site data blocked. An unreadable store means "show
 * it" — a tour shown twice is a small annoyance, and a screen that fails to
 * render because it could not read a preference is not.
 */
function alreadySeen(): boolean {
  try {
    return globalThis.localStorage?.getItem(SEEN) === "yes";
  } catch {
    return false;
  }
}

function remember(): void {
  try {
    globalThis.localStorage?.setItem(SEEN, "yes");
  } catch {
    // Nothing to do: it will be offered again, which is the harmless failure.
  }
}

export function FirstRunTour(): ReactNode {
  // Read once on mount rather than during render, so the first paint is the
  // same on the server and in the browser and React does not warn about it.
  const [show, setShow] = useState(false);
  const [card, setCard] = useState(0);

  useEffect(() => {
    if (!alreadySeen()) setShow(true);
  }, []);

  if (!show) return null;

  const close = (): void => {
    remember();
    setShow(false);
  };

  const current = CARDS[card]!;
  const last = card === CARDS.length - 1;

  return (
    <aside className="tour" aria-label="Getting started" data-testid="first-run-tour">
      <p className="tour__step">
        Step {card + 1} of {CARDS.length}
      </p>
      <h2 className="tour__title">{current.title}</h2>
      <p className="tour__body">{current.body}</p>

      <div className="tour__actions">
        <button
          type="button"
          className="primary"
          onClick={() => (last ? close() : setCard(card + 1))}
        >
          {last ? "Got it" : "Next"}
        </button>
        <button type="button" className="secondary" onClick={close}>
          Skip
        </button>
      </div>
    </aside>
  );
}
